import { useCallback, useEffect, useRef, useState } from 'react';

import { useSearchFocusState } from '@/hooks/useSearchFocusState';

import type { Editor as TiptapEditor } from '@tiptap/core';

const MAX_SEARCH_FOCUS_RETRIES = 12;
const SEARCH_FOCUS_RETRY_DELAY_MS = 250;

interface UseEditorSearchFocusInput {
  editor: TiptapEditor | null;
  editorId: string;
}

export function useEditorSearchFocus({
  editor,
  editorId,
}: UseEditorSearchFocusInput) {
  const { clearSearchFocusParams, searchFocus } = useSearchFocusState({
    editorId,
  });
  const appliedFocusKeyRef = useRef<string | undefined>(undefined);
  const retryStateRef = useRef<{ focusKey?: string; count: number }>({
    focusKey: undefined,
    count: 0,
  });
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  const clearRetryTimer = useCallback(() => {
    if (!retryTimerRef.current) {
      return;
    }

    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      clearRetryTimer();
    };
  }, [clearRetryTimer]);

  useEffect(() => {
    if (!searchFocus.focusQuery) {
      appliedFocusKeyRef.current = undefined;
      retryStateRef.current = { focusKey: undefined, count: 0 };
      clearRetryTimer();
      return;
    }

    if (!editor) {
      return;
    }

    if (searchFocus.focusBucketId) {
      return;
    }

    if (!searchFocus.focusEditorId || searchFocus.focusEditorId !== editorId) {
      return;
    }

    const occurrence = Math.max(searchFocus.focusOccurrence ?? 0, 0);
    const focusKey = `${searchFocus.focusEditorId}:${searchFocus.focusQuery}:${occurrence}`;

    if (retryStateRef.current.focusKey !== focusKey) {
      retryStateRef.current = { focusKey, count: 0 };
      clearRetryTimer();
    }

    if (appliedFocusKeyRef.current === focusKey) {
      clearRetryTimer();
      retryStateRef.current.count = 0;
      return;
    }

    let didFocus = false;

    try {
      didFocus =
        editor.commands.focusSearchResult?.({
          searchTerm: searchFocus.focusQuery,
          occurrence,
          caseSensitive: false,
        }) ?? false;
    } catch (error) {
      didFocus = false;
      console.warn('useEditorSearchFocus: focusSearchResult failed', error);
    }

    if (didFocus) {
      clearRetryTimer();
      retryStateRef.current.count = 0;
      appliedFocusKeyRef.current = focusKey;
      clearSearchFocusParams();
      return;
    }

    const nextRetryCount = retryStateRef.current.count + 1;
    retryStateRef.current.count = nextRetryCount;

    if (nextRetryCount <= MAX_SEARCH_FOCUS_RETRIES) {
      clearRetryTimer();
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        setRetryToken((value) => value + 1);
      }, SEARCH_FOCUS_RETRY_DELAY_MS);
      return;
    }

    clearRetryTimer();
    retryStateRef.current.count = 0;
    clearSearchFocusParams();
  }, [
    clearRetryTimer,
    clearSearchFocusParams,
    editor,
    editorId,
    searchFocus.focusBucketId,
    searchFocus.focusEditorId,
    searchFocus.focusOccurrence,
    searchFocus.focusQuery,
    retryToken,
  ]);
}
